import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Loader } from "lucide-react";
import { toast } from "react-hot-toast";
import {
  checkDomainAvailability,
  registerHospitalRequest,
  verifyHospitalRegistrationOtp,
  checkHospitalNameExistence,
} from "../../services/hospital.service";
const RegisterHospital = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    hospital: {
      hospitalName: "",
      registrationNumber: "",
      domainName: "",
      officialEmail: "",
      officialPhone: "",
      addressline1: "",
      addressline2: "",
      city: "",
      country: "",
      postcode: "",
      subscriptionPlanId: "",
    },
    admin: {
      name: "",
      email: "",
      password: "",
      confirmPassword: "",
    },
  });

  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState("");
  const [devOtp, setDevOtp] = useState("");
  const [showOtp, setShowOtp] = useState(false);
  const otpInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (showOtp) {
      otpInputRef.current?.focus();
    }
  }, [showOtp]);

  useEffect(() => {
    if (showOtp && devOtp && process.env.NODE_ENV !== "production") {
      console.log(
        "%c 🔐 Registration OTP Code ",
        "background: #2563eb; color: #ffffff; border-radius: 4px; font-size: 14px; font-weight: bold; padding: 4px 8px;",
        `Your registration verification code is: ${devOtp}`
      );
    }
  }, [showOtp, devOtp]);

  const [domainStatus, setDomainStatus] = useState("idle");
  const domainNameDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [hospitalNameStatus, setHospitalNameStatus] = useState<
    "idle" | "checking" | "available" | "taken"
  >("idle");

  const hospitalNameDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    section: "hospital" | "admin"
  ) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [e.target.name]: e.target.value,
      },
    }));
  };

  const handleDomainChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLowerCase().replace(/[^a-z0-9]/g, "");

    setFormData((prev) => ({
      ...prev,
      hospital: {
        ...prev.hospital,
        domainName: value,
      },
    }));
  };

  const ERROR_MESSAGES: Record<string, string> = {
    DOMAIN_REQUIRED: "Hospital domain name is required",
    HOSPITAL_EMAIL_REQUIRED: "Official hospital email is required",
    PASSWORD_AND_CONFIRM_REQUIRED: "Password and confirm password are required",
    PASSWORD_MISMATCH: "Passwords do not match",
    INVALID_HOSPITAL_EMAIL_FORMAT: "Invalid hospital email format",
    INVALID_ADMIN_EMAIL_FORMAT: "Invalid admin email format",
    DOMAIN_ALREADY_EXISTS: "This domain name is already registered",
    HOSPITAL_EMAIL_ALREADY_EXISTS: "This hospital email is already in use",
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    if (formData.admin.password !== formData.admin.confirmPassword) {
      const message = ERROR_MESSAGES.PASSWORD_MISMATCH;
      toast.error(message);
      setLoading(false);
      return;
    }

    if (hospitalNameStatus === "taken") {
      toast.error("Hospital name already exists");
      setLoading(false);
      return;
    }

    if (domainStatus === "taken") {
      setLoading(false);
      return;
    }

    try {
      const response = await registerHospitalRequest({
        ...formData,
        hospital: {
          ...formData.hospital,
          subscriptionPlanId: formData.hospital.subscriptionPlanId,
        },
      });

      const otpCode = response?.otp || response?.data?.otp || response?.mfa?.otp;
      if (otpCode && process.env.NODE_ENV !== "production") {
        setDevOtp(otpCode);
      }

      toast.success("OTP sent to admin email");

      setShowOtp(true);
    } catch (err: any) {
      const backendCode = err?.response?.data?.message;

      const message =
        ERROR_MESSAGES[backendCode] || "Hospital registration failed";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    try {
      setLoading(true);

      const res = await verifyHospitalRegistrationOtp({
        adminEmail: formData.admin.email,
        otp,
      });

      if (res.token) {
        localStorage.setItem("temp_subscription_token", res.token);
      }

      localStorage.setItem(
        "hospital_registration_preview",
        JSON.stringify({
          hospital: {
            hospitalName: formData.hospital.hospitalName,
            registrationNumber: formData.hospital.registrationNumber,
            domainName: formData.hospital.domainName,
            officialEmail: formData.hospital.officialEmail,
            officialPhone: formData.hospital.officialPhone,
            addressline1: formData.hospital.addressline1,
            addressline2: formData.hospital.addressline2,
            city: formData.hospital.city,
            country: formData.hospital.country,
            postcode: formData.hospital.postcode,
          },
          admin: {
            name: formData.admin.name,
            email: formData.admin.email,
          },
        })
      );

      setShowOtp(false);
      setOtp("");
      setDevOtp("");

      toast.success("OTP verified successfully. Please select your subscription plan.");
      navigate("/subscription");
    } catch (err: any) {
      const message = err?.response?.data?.message || err.message || "OTP verification failed";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (showOtp) return;

    const domain = formData.hospital.domainName;

    if (!domain) {
      setDomainStatus("idle");
      return;
    }

    setDomainStatus("checking");

    if (domainNameDebounceRef.current) {
      clearTimeout(domainNameDebounceRef.current);
    }

    domainNameDebounceRef.current = setTimeout(async () => {
      try {
        const res = await checkDomainAvailability(domain);

        setDomainStatus(res.available ? "available" : "taken");
      } catch (error) {
        setDomainStatus("idle");
      }
    }, 500);

    return () => {
      if (domainNameDebounceRef.current) {
        clearTimeout(domainNameDebounceRef.current);
      }
    };
  }, [formData.hospital.domainName, showOtp]);

  useEffect(() => {
    if (showOtp) return;

    const name = formData.hospital.hospitalName.trim();

    if (!name) {
      setHospitalNameStatus("idle");
      return;
    }

    setHospitalNameStatus("checking");

    if (hospitalNameDebounceRef.current) {
      clearTimeout(hospitalNameDebounceRef.current);
    }

    hospitalNameDebounceRef.current = setTimeout(async () => {
      try {
        const res = await checkHospitalNameExistence(name);
        setHospitalNameStatus(res.available ? "available" : "taken");
      } catch {
        setHospitalNameStatus("idle");
      }
    }, 500);

    return () => {
      if (hospitalNameDebounceRef.current) {
        clearTimeout(hospitalNameDebounceRef.current);
      }
    };
  }, [formData.hospital.hospitalName, showOtp]);

  return (
    <div className="min-h-screen w-full bg-gray-50 overflow-x-hidden flex flex-col px-4 py-4 sm:px-6 lg:px-10 relative selection:bg-gray-900 selection:text-white">

      {/* Top Center Logo */}
      <div className="relative z-10 flex justify-center pt-2 pb-3">
        <img
          src="/logo/DiagnomIQ.png"
          alt="DiagnomIQ"
          className="h-12 w-auto object-contain sm:h-14 lg:h-16"
        />
      </div>

      {/* Header - Centered & Concise */}
      <div className="w-full flex flex-col items-center justify-center shrink-0 mb-6 sm:mb-8 mt-1">
        <h1 className="text-3xl font-bold text-gray-900 tracking-tight">
          Hospital Registration
        </h1>
        <p className="text-gray-500 text-sm mt-1 font-medium">
          Create your administrative account
        </p>
      </div>

      {/* Main Form Area - Fixed, No Scroll, 3-Column Grid */}
      <form
        onSubmit={handleSubmit}
        className="flex-1 w-full max-w-[1600px] mx-auto grid grid-cols-12 gap-6 sm:gap-8 lg:gap-12 content-start items-start pb-10"
      >

        {/* COLUMN 1: Hospital Details (4 cols) */}
        <div className="col-span-12 lg:col-span-4 flex flex-col gap-5">
          <h3 className="text-sm font-bold text-gray-900 uppercase tracking-widest border-b border-gray-200 pb-2 mb-1">
            Hospital Details
          </h3>

          <div className="flex flex-col gap-4">
            <FormField
              label="Hospital Name"
              name="hospitalName"
              value={formData.hospital.hospitalName}
              onChange={(e) => handleChange(e, "hospital")}
              bottomMessage={
                hospitalNameStatus !== "idle" && (
                  <div className="absolute top-full left-0 mt-0.5 text-[10px] whitespace-nowrap">
                    {hospitalNameStatus === "checking" && (
                      <span className="text-gray-400">Checking...</span>
                    )}
                    {hospitalNameStatus === "taken" && (
                      <span className="text-red-500 font-medium">Taken</span>
                    )}
                    {hospitalNameStatus === "available" && (
                      <span className="text-green-600 font-medium">Available</span>
                    )}
                  </div>
                )
              }
            />

            <FormField
              label="Registration No."
              name="registrationNumber"
              value={formData.hospital.registrationNumber}
              onChange={(e) => handleChange(e, "hospital")}
            />

            <FormField
              label="Domain Name"
              name="domainName"
              placeholder="e.g. citycare"
              value={formData.hospital.domainName}
              onChange={handleDomainChange}
              bottomMessage={
                domainStatus !== "idle" && (
                  <div className="absolute top-full left-0 mt-0.5 text-[10px] whitespace-nowrap">
                    {domainStatus === "checking" && (
                      <span className="text-gray-400">Checking...</span>
                    )}
                    {domainStatus === "taken" && (
                      <span className="text-red-500 font-medium">Taken</span>
                    )}
                    {domainStatus === "available" && formData.hospital.domainName && (
                      <span className="text-green-600 font-medium">Available</span>
                    )}
                  </div>
                )
              }
            />

            <FormField
              label="Official Email"
              name="officialEmail"
              type="email"
              value={formData.hospital.officialEmail}
              onChange={(e) => handleChange(e, "hospital")}
            />

            <FormField
              label="Official Phone"
              name="officialPhone"
              value={formData.hospital.officialPhone}
              onChange={(e) => handleChange(e, "hospital")}
            />
          </div>
        </div>

        {/* COLUMN 2: Address Details (4 cols) */}
        <div className="col-span-12 lg:col-span-4 flex flex-col gap-5">
          <h3 className="text-sm font-bold text-gray-900 uppercase tracking-widest border-b border-gray-200 pb-2 mb-1">
            Location
          </h3>

          <div className="flex flex-col gap-4">
            <FormField
              label="Address Line 1"
              name="addressline1"
              value={formData.hospital.addressline1}
              onChange={(e) => handleChange(e, "hospital")}
            />

            <FormField
              label="Address Line 2"
              name="addressline2"
              value={formData.hospital.addressline2}
              onChange={(e) => handleChange(e, "hospital")}
            />

            <FormField
              label="City"
              name="city"
              value={formData.hospital.city}
              onChange={(e) => handleChange(e, "hospital")}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                label="Country"
                name="country"
                value={formData.hospital.country}
                onChange={(e) => handleChange(e, "hospital")}
              />

              <FormField
                label="Postcode"
                name="postcode"
                value={formData.hospital.postcode}
                onChange={(e) => handleChange(e, "hospital")}
              />
            </div>
          </div>
        </div>


        {/* COLUMN 3: Admin & Submit (4 cols) */}
        <div className="col-span-12 lg:col-span-4 flex flex-col h-full justify-between">

          <div className="flex flex-col gap-5">
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-widest border-b border-gray-200 pb-2 mb-1">
              Admin Access
            </h3>

            <div className="flex flex-col gap-4">
              <FormField
                label="Admin Name"
                name="name"
                value={formData.admin.name}
                onChange={(e) => handleChange(e, "admin")}
              />

              <FormField
                label="Admin Email"
                name="email"
                type="email"
                value={formData.admin.email}
                onChange={(e) => handleChange(e, "admin")}
              />

              {/* <div className="grid grid-cols-2 gap-4">
                <FormField
                  label="Password"
                  name="password"
                  type="password"
                  value={formData.admin.password}
                  onChange={(e) => handleChange(e, "admin")}
                />

                <FormField
                  label="Confirm"
                  name="confirmPassword"
                  type="password"
                  value={formData.admin.confirmPassword}
                  onChange={(e) => handleChange(e, "admin")}
                />
              </div> */}
            </div>
          </div>

          {/* Submit Button Area */}
          <div className="mt-8 lg:mt-auto pt-6">
            <button
              type="submit"
              disabled={
                loading ||
                domainStatus === "checking" || domainStatus === "taken" ||
                hospitalNameStatus === "checking" || hospitalNameStatus === "taken"
              }
              className="group relative w-full flex justify-center py-2.5 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-500 hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading && <Loader className="h-4 w-4 animate-spin text-white mr-2" />}
              Register
            </button>
            <p className="text-[10px] text-gray-400 text-center mt-3">
              By registering, you agree to our Terms of Service & Privacy Policy.
            </p>
          </div>

        </div>

      </form>

      {/* OTP Modal Overlay */}
      {showOtp && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 backdrop-blur-sm p-4">
          <div className="bg-white p-8 rounded-xl shadow-2xl w-full max-w-sm border border-gray-100 transform transition-all animate-in fade-in zoom-in-95 duration-200">
            <div className="text-center mb-6">
              <h3 className="text-xl font-bold text-gray-900">Verify Email</h3>
              <p className="text-gray-500 text-xs mt-2">
                Code sent to <span className="font-medium text-gray-700">{formData.admin.email}</span>
              </p>
            </div>

            <div className="space-y-4">
              <input
                ref={otpInputRef}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="000000"
                className="w-full text-center text-3xl tracking-[0.5em] font-bold border-b-2 border-neutral-200 focus:border-primary-500 outline-none py-2 bg-transparent transition-colors placeholder:text-neutral-200"
                maxLength={6}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleVerifyOtp();
                  }
                }}
              />
              <button
                type="button"
                onClick={handleVerifyOtp}
                className="w-full py-2.5 bg-primary-500 text-white rounded-lg font-semibold text-sm hover:bg-primary-600 transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
              >
                {loading ? "Verifying..." : "Verify Code"}
              </button>
              <button
                onClick={() => {
                  setShowOtp(false);
                  setOtp("");
                  setDevOtp("");
                }}
                className="w-full py-2 text-xs text-neutral-400 hover:text-neutral-800 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dev/QA OTP badge — only visible in non-production */}
      {showOtp && devOtp && process.env.NODE_ENV !== "production" && (
        <div className="fixed bottom-4 right-4 z-50 bg-gray-900/90 text-white px-4 py-2 rounded-lg shadow-lg text-sm font-mono backdrop-blur-sm">
          <span className="opacity-60">Dev OTP:</span>{" "}
          <span className="font-bold tracking-widest">{devOtp}</span>
        </div>
      )}

    </div>
  );
};

/* ---------- Compact Boxed Input ---------- */
interface FormFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  bottomMessage?: React.ReactNode;
}

const FormField = ({ label, bottomMessage, ...props }: FormFieldProps) => (
  <div className="group w-full relative">
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label}
      {!label.includes("Optional") && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    <div className="relative">
      <input
        required={!label.includes("Optional")}
        className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm bg-white text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-500 transition-colors"
        {...props}
      />
    </div>
    {bottomMessage}
  </div>
);

export default RegisterHospital;
